import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios"
import { handleRevokedSession, isRevokedSessionError } from "../utils/session-state"
import { API_ENDPOINTS } from "./endpoints"

/**
 * HTTP Client with Automatic Token Refresh
 *
 * Centralized Axios instance for all API requests.
 * Infrastructure-level client - no domain logic should be added here.
 *
 * Features:
 * - Automatic access token injection in request headers
 * - Automatic token refresh on 401/403 errors (expired token)
 * - Token rotation support (backend returns new refresh_token on each refresh)
 * - Request queuing during token refresh
 * - Infinite loop prevention for auth endpoints
 *
 * Note: Backend returns 403 Forbidden with {"detail":"Access token has expired"}
 *       when access token expires, not 401 Unauthorized.
 */

// API Base URL - single source of truth
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001/"

// Create axios instance
const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout for Auth0 redirects
  headers: {
    "Content-Type": "application/json",
  },
})

// Flag to prevent infinite refresh loops
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Request interceptor to add auth token
httpClient.interceptors.request.use(
  async (config) => {
    // Get token from sessionStorage (set by auth service)
    const token = sessionStorage.getItem("access_token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling with automatic token refresh
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // A session revoked server-side (e.g. the user's department was deactivated
    // or reactivated) cannot be refreshed — send them to login with a notice
    // instead of attempting a refresh that would loop.
    if (isRevokedSessionError(error)) {
      handleRevokedSession()
      return Promise.reject(error)
    }

    // Check if token is expired (401 or 403 with expired token message)
    const isTokenExpired =
      error.response?.status === 401 ||
      (error.response?.status === 403 &&
        error.response?.data?.detail?.toLowerCase().includes("token") &&
        (error.response?.data?.detail?.toLowerCase().includes("expired") ||
          error.response?.data?.detail?.toLowerCase().includes("invalid")))

    // If error is 401/403 (expired token) and we haven't tried to refresh yet
    if (isTokenExpired && !originalRequest._retry) {
      // Skip refresh for auth endpoints to prevent infinite loops
      const url = originalRequest.url || ""
      if (
        url.includes(API_ENDPOINTS.auth.login) ||
        url.includes(API_ENDPOINTS.auth.refresh) ||
        url.includes(API_ENDPOINTS.auth.logout) ||
        url.includes(API_ENDPOINTS.auth.callback)
      ) {
        console.error("[HTTP Client] Auth endpoint failed, clearing tokens and redirecting to home")
        sessionStorage.removeItem("access_token")
        sessionStorage.removeItem("refresh_token")
        sessionStorage.removeItem("user")
        window.location.href = "/home"
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return httpClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = sessionStorage.getItem("refresh_token")

      if (!refreshToken) {
        console.error("[HTTP Client] No refresh token available, redirecting to home")
        // No refresh token available, redirect to home
        sessionStorage.removeItem("access_token")
        sessionStorage.removeItem("refresh_token")
        sessionStorage.removeItem("user")
        isRefreshing = false
        window.location.href = "/home"
        return Promise.reject(error)
      }

      try {
        // Attempt to refresh the token using a fresh axios instance
        const response = await axios.post(
          `${API_BASE_URL}${API_ENDPOINTS.auth.refresh}`,
          { refresh_token: refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000, // 10 second timeout for refresh
          }
        )

        const { access_token, refresh_token: newRefreshToken } = response.data

        if (!access_token) {
          throw new Error("No access token in refresh response")
        }

        // Update stored tokens (token rotation - backend returns new refresh_token)
        sessionStorage.setItem("access_token", access_token)

        if (newRefreshToken) {
          sessionStorage.setItem("refresh_token", newRefreshToken)
          console.log("[HTTP Client] Token refresh successful")
        }

        // Fetch and store fresh user data after token refresh
        try {
          const userResponse = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.auth.status}`, {
            headers: {
              Authorization: `Bearer ${access_token}`,
              "Content-Type": "application/json",
            },
            timeout: 5000,
          })

          // Store fresh user data
          if (userResponse.data.user) {
            sessionStorage.setItem("user", JSON.stringify(userResponse.data.user))
          } else if (userResponse.data.email) {
            sessionStorage.setItem("user", JSON.stringify(userResponse.data))
          }

          // Dispatch custom event to notify components of refresh
          const event = new CustomEvent("auth-refreshed", {
            detail: {
              accessToken: access_token,
              refreshToken: newRefreshToken,
              user: userResponse.data.user || userResponse.data,
              timestamp: Date.now(),
            },
          })
          window.dispatchEvent(event)
        } catch (userError) {
          console.warn("[HTTP Client] Failed to fetch user data after token refresh:", userError)
          // Continue even if user fetch fails - token refresh was successful
        }

        // Update the failed request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }

        // Process queued requests with new token
        processQueue(null, access_token)

        // Retry original request
        return httpClient(originalRequest)
      } catch (refreshError: any) {
        const errorDetail = refreshError.response?.data?.detail || refreshError.message
        console.error("[HTTP Client] Token refresh failed:", errorDetail)

        // Check if it's an expired refresh token
        if (
          refreshError.response?.status === 400 ||
          refreshError.response?.status === 401 ||
          refreshError.response?.status === 403
        ) {
          if (errorDetail?.includes("expired") || errorDetail?.includes("Expired")) {
            console.warn("[HTTP Client] Refresh token has expired. User needs to log in again.")
          }
        }

        // Refresh failed, clear tokens and redirect to login
        processQueue(refreshError as Error, null)
        sessionStorage.removeItem("access_token")
        sessionStorage.removeItem("refresh_token")
        sessionStorage.removeItem("user")

        // Only redirect if not already on the landing page
        if (!window.location.pathname.includes("/home")) {
          window.location.href = "/home"
        }

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

/**
 * API Client Class
 *
 * Wrapper around the httpClient instance with convenience methods.
 * Use this for making typed API calls in service files.
 *
 * Note: API versioning is handled in endpoints.ts - each endpoint
 * includes its version prefix (e.g., "v1/auth/login/", "v2/account/status/")
 */
export class HttpClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = httpClient
  }

  // Generic request methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.instance.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.put(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.patch(url, data)
    return response.data
  }

  async delete<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.delete(url, { data })
    return response.data
  }

  // Set auth token
  setAuthToken(token: string) {
    this.instance.defaults.headers.common["Authorization"] = `Bearer ${token}`
    sessionStorage.setItem("access_token", token)
  }

  // Clear auth token
  clearAuthToken() {
    delete this.instance.defaults.headers.common["Authorization"]
    sessionStorage.removeItem("access_token")
    sessionStorage.removeItem("refresh_token")
    sessionStorage.removeItem("user")
  }
}

// Create singleton instance
export const httpClientInstance = new HttpClient()

// Export the raw axios instance for advanced use cases
export default httpClient
